<template>
  <div
    class="wt-lightbox-asset -audio-asset"
    :class="waveFormClass"
    @click="togglePlay"
  >
    <audio
      ref="audio"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @ended="onAudioEnded"
      @play="playing = true"
      @pause="playing = false"
      @emptied="playing = false"
    />

    <div
      v-if="!transcribing && transcript.length"
      class="-transcript"
      dir="auto"
      v-text="transcript"
    />

    <div v-if="canAdd" class="-actions">
      <wt-asset-locate @locate="locateAssetOnTimeline" :asset="segment" />
      <wt-asset-remove @remove="removeAsset" />
      <wt-asset-transcribe @transcribe="transcribe" />
      <wt-asset-edit @edit="$emit('edit')" />
      <wt-asset-add :disabled="isSceneEdit" @add="$emit('add')" />
    </div>

    <template v-if="duration > 0">
      <span v-if="playing" class="-play wticon wticon-pause" />
      <span v-else class="-play wticon wticon-play1" />

      <div class="-progress" :style="progressStyle" />
    </template>

    <div class="-bottom">
      <div class="-title wt2-text-s">
        <span v-if="title" :title="title" v-text="title" />
        <span v-else class="wticon wticon-mic" />
      </div>
      <div class="-duration" v-text="durationText" />
    </div>

    <div class="-indicators">
      <transition name="wt2-fade-fast">
        <wt-asset-used v-if="isUsed" />
      </transition>
      <div v-if="transcribing" class="-transcribing">
        Transcribing<wt-ellipsis-animation />
      </div>
    </div>
  </div>
</template>

<script>
import AssetUsed from '../lightbox/AssetUsed.vue';
import AssetAdd from '../mixins/AssetAdd.vue';
import AssetEdit from '../mixins/AssetEdit.vue';
import AssetRemove from '../mixins/AssetRemove.vue';
import AssetTranscribe from '../mixins/AssetTranscribe.vue';
import AssetLocate from '../mixins/AssetLocate.vue';
import storyViewBus from '../../../views/Story/storyViewBus.js';
import app from 'app';
import wtTranscribe from 'views/Transcribe/Transcribe.vue';
import { mapState, mapActions } from 'vuex';
import moment from 'moment';

export default {
  name: 'wtAudioAsset',
  components: {
    'wt-asset-add': AssetAdd,
    'wt-asset-used': AssetUsed,
    'wt-asset-remove': AssetRemove,
    'wt-asset-transcribe': AssetTranscribe,
    'wt-asset-locate': AssetLocate,
    'wt-asset-edit': AssetEdit,
  },

  props: {
    segment: {
      type: Object,
      required: true,
    },
    canAdd: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      playing: false,
      duration: 0,
      currentTime: 0,
      lastPauseTime: 0,
    };
  },

  computed: {
    ...mapState({
      transcriptions: (state) => state.story.transcriptions,
    }),

    assetUUID() {
      return this.segment.audioSegmentContent.asset.metadata.assetUUID;
    },

    transcribing() {
      return /PENDING|PROCESSING/.test(this.currentTranscript?.status);
    },

    durationText() {
      return moment.utc(this.asc.asset.metadata.duration * 1000).format('m:ss');
    },

    transcript() {
      let transcript = '';
      const tokensLength = this.currentTranscript?.tokens?.length;
      if (tokensLength) {
        _.each(this.currentTranscript?.tokens, (token, tokenIndex) => {
          if (token.type === 'WORD') {
            transcript += ` ${token.content}`;
          }
          if (tokenIndex === 12) {
            transcript += '...';
            return false;
          }
        });
      }
      return transcript;
    },

    asc() {
      return this.segment.audioSegmentContent;
    },

    src() {
      return this.asc.cdnUrl;
    },

    isUsed() {
      return this.segment._isInTimeline;
    },

    progressStyle() {
      const time = this.playing ? this.currentTime : this.lastPauseTime;
      const progress = app.sfp((time / this.duration) * 100, 1e3);
      const width = `${progress}%`;
      return {
        width,
      };
    },

    title() {
      return this.asc.asset.metadata.title || 'REC 01';
    },

    waveFormClass() {
      const ar = [];
      if (!this.transcript.length || this.transcribing) {
        ar.push(this.playing ? '-wave-form-blue' : '-wave-form-grey');
      }
      if (this.segment._deleted) {
        ar.push('-deleted');
      }
      return ar;
    },

    currentTranscript() {
      const assetUUID = this.assetUUID;
      return this.transcriptions[assetUUID];
    },

    isSceneEdit() {
      return this.MODE_SCENE_EDIT;
    },
  },

  beforeDestroy() {
    storyViewBus.neverMind(this);
    document.removeEventListener('click', this.$_onDocumentClick);
    document.removeEventListener('keydown', this.$_onDocumentKeydown);
  },

  mounted() {
    storyViewBus.mind(this, ['svbPauseRcp']);
    document.addEventListener('click', this.$_onDocumentClick);
    document.addEventListener('keydown', this.$_onDocumentKeydown);
  },

  methods: {
    locateAssetOnTimeline() {
      this.$emit('locate', this.segment);
    },

    transcribe() {
      this.$refs.audio.pause();
      storyViewBus.$emit('svbPauseRcp');
      app.showModal({
        type: 'custom',
        component: wtTranscribe,
        showCloseButton: false,
        closeOnEscape: true,
        wrapperClass: ['wt-transcribe-modal'],
        store: app.rootStore,
        componentPropsData: {
          opener: app.TRANSCRIPT_OPENER.LIGHTBOX_MENU,
          list: [
            {
              assetUUID: this.assetUUID,
              transcribeSubType: app.transcribeSubType.NARRATION,
              timeLimit: false, // {startTime: 0, endTime: 1},
            },
          ],
        },
      });
    },

    $_onDocumentClick(e) {
      if (e && !this.$el.contains(e.target)) {
        this.svbPauseRcp();
      }
    },

    $_onDocumentKeydown(e) {
      if (e.key === ' ') {
        this.svbPauseRcp();
      }
    },

    svbPauseRcp() {
      if (!this.$refs.audio.paused) {
        this.$refs.audio.pause();
        this.lastPauseTime = this.$refs.audio.currentTime;
        this.$refs.audio.src = '';
      }
    },

    togglePlay(e) {
      const audio = this.$refs.audio;
      if (e.target.closest('.-action') || !audio) {
        return;
      }

      if (e?.metaKey) {
        this.$log(this.segment, this.currentTranscript);
      } else if (this.playing) {
        this.$refs.audio.pause();
        this.lastPauseTime = audio.currentTime;
        audio.src = '';
      } else {
        storyViewBus.$emit('svbPauseRcp');
        audio.src = this.mixinSrc(this.src);
        audio.addEventListener('canplay', this.onCanPlay);
        this.$emit('locate', this.segment);
      }
    },

    onCanPlay() {
      const audio = this.$refs.audio;
      if (!audio) {
        return;
      }
      audio.currentTime = this.lastPauseTime;
      audio.play();
      audio.removeEventListener('canplay', this.onCanPlay);
    },

    onLoadedMetadata() {
      if (!this.$refs.audio) {
        return;
      }
      this.duration = this.$refs.audio.duration;
    },

    onTimeUpdate() {
      if (!this.$refs.audio) {
        return;
      }
      this.currentTime = this.$refs.audio.currentTime;
    },

    onAudioEnded() {
      if (!this.$refs.audio) {
        return;
      }
      this.lastPauseTime = 0;
      this.$refs.audio.src = '';
    },

    async removeAsset() {
      if (this.isUsed) {
        storyViewBus.$emit('svbPauseRcp');
        const data = await app.dialog.confirm({
          type: app.dialog.types.CONFIRM_3,
          title: 'Are you sure?',
          description:
            'This audio is in use on timeline. Deleting it will remove it from the timeline.',
          buttonYesText: 'Yes',
          buttonNoText: app.culture.generic.CANCEL,
          buttonYesAction: true,
        });
        if (data.action) {
          this.$emit('remove');
        }
      } else {
        this.$emit('remove');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-shadow: -2px 2px 10px rgba($black, 0.3);
  color: #fff;
  font-size: 20px;
  z-index: 1;
}
.-audio-asset {
  cursor: pointer;
  background-color: white;
  background-repeat: no-repeat;
  background-position: center center;
  &.-deleted {
    display: none;
  }
}
.-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: $color-primary;
}
.-transcript {
  width: calc(100% - 20px);
  height: calc(100% - 32px);
  position: absolute;
  left: 10px;
  top: 8px;
  font-size: 11px;
  overflow: hidden;
  -webkit-line-clamp: 4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  line-height: 1.2;
}
.-transcribing {
  color: $color-normal;
  margin-left: 6px;
  font-size: 11px;
}
.-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 26px;
  display: flex;
}
.-duration {
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 5px;
  padding-left: 5px;
  width: 47px;
}
.-title {
  > span {
    display: inline-block;
    height: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  padding: 0 5px;
  width: calc(100% - 47px);
  display: flex;
  align-items: center;
}
.wticon-mic {
  color: rgba(0, 0, 0, 0.75);
}
/* eslint-disable-next-line */
.-wave-form-grey {
  background-size: auto 36px;
  background-image: url('/resources/img/soundwave_grey.png');
}
/* eslint-disable-next-line */
.-wave-form-blue {
  background-size: auto 36px;
  background-image: url('/resources/img/soundwave_blue.png');
}
</style>
